import { c as _c } from "react/compiler-runtime";
import { SubheadingH3 } from '@/components/ui/typography/subheading-h3';
import { fields as fieldComponents } from '@/features/payload-cms/components/form/fields';
import type { ConditionedBlock, DateSlotSelectionBlock, FormFieldBlock, FormSection, HofSelectionBlock, JobSelectionBlock } from '@/features/payload-cms/components/form/types';
import { getEffectivePlacement } from '@/features/payload-cms/components/form/utils/field-placement';
import React, { createContext, useContext, useEffect } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';

/** Fields whose answer the embedding feature presets, so the form does not ask for them. */
export const HiddenFieldsContext = createContext<ReadonlySet<string>>(new Set());
interface FormFieldRendererProperties {
  section: FormSection;
  currentStepIndex: number;
  formId: string | undefined;
  renderMode?: 'all' | 'sidebar' | 'main';
}
const ConditionedField: React.FC<{
  block: ConditionedBlock;
  currentStepIndex: number;
  formId: string | undefined;
  renderMode: 'all' | 'sidebar' | 'main';
}> = t0 => {
  const $ = _c(19);
  const {
    block,
    currentStepIndex,
    formId,
    renderMode
  } = t0;
  const {
    control,
    resetField
  } = useFormContext();
  let t1;
  if ($[0] !== block.displayCondition.field || $[1] !== control) {
    t1 = {
      control,
      name: block.displayCondition.field
    };
    $[0] = block.displayCondition.field;
    $[1] = control;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const conditionedValue = useWatch(t1) as string | number | boolean | undefined;
  const isVisible = String(conditionedValue ?? "") === block.displayCondition.value;
  let t2;
  let t3;
  if ($[3] !== block.fields || $[4] !== isVisible || $[5] !== resetField) {
    t2 = () => {
      if (!isVisible) {
        for (const f of block.fields) {
          if ("name" in f && typeof f.name === "string" && f.name !== "") {
            resetField(f.name, {
              defaultValue: undefined
            });
          }
          if (f.blockType === "dateSlotSelection" && typeof f.ressortName === "string" && f.ressortName !== "") {
            resetField(f.ressortName, {
              defaultValue: undefined
            });
          }
        }
      }
    };
    t3 = [isVisible, block.fields, resetField];
    $[3] = block.fields;
    $[4] = isVisible;
    $[5] = resetField;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  if (!isVisible) {
    return null;
  }
  let t4;
  if ($[8] !== block.fields || $[9] !== currentStepIndex || $[10] !== formId || $[11] !== renderMode) {
    let t5;
    if ($[13] !== currentStepIndex || $[14] !== formId || $[15] !== renderMode) {
      t5 = (field, index) => <SingleField key={("id" in field && typeof field.id === "string" && field.id !== "" ? field.id : undefined) ?? index} field={field} currentStepIndex={currentStepIndex} formId={formId} renderMode={renderMode} />;
      $[13] = currentStepIndex;
      $[14] = formId;
      $[15] = renderMode;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    t4 = block.fields.map(t5);
    $[8] = block.fields;
    $[9] = currentStepIndex;
    $[10] = formId;
    $[11] = renderMode;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[17] !== t4) {
    t5 = <>{t4}</>;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
};
const SingleField: React.FC<{
  field: (FormFieldBlock | JobSelectionBlock | HofSelectionBlock | DateSlotSelectionBlock) & {
    required?: boolean;
  };
  currentStepIndex: number;
  formId: string | undefined;
  renderMode: 'all' | 'sidebar' | 'main';
}> = t0 => {
  const $ = _c(11);
  const {
    field,
    currentStepIndex,
    formId,
    renderMode
  } = t0;
  const Component = fieldComponents[field.blockType];
  const {
    register,
    control
  } = useFormContext();
  const hiddenFields = useContext(HiddenFieldsContext);
  const fieldName = "name" in field && typeof field.name === "string" ? field.name : undefined;
  const t1 = fieldName ?? "";
  let t2;
  if ($[0] !== t1) {
    t2 = {
      name: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    errors
  } = useFormState(t2);
  const fieldError = fieldName !== undefined && fieldName !== "" ? errors[fieldName] : undefined;
  if (!Component) {
    console.error(`Field type ${field.blockType} is not supported`);
    return null;
  }
  if (fieldName !== undefined && hiddenFields.has(fieldName)) {
    return null;
  }
  const effectivePlacement = getEffectivePlacement(field);
  if (renderMode !== "all" && renderMode !== effectivePlacement) {
    return null;
  }
  let t3;
  if ($[2] !== Component || $[3] !== control || $[4] !== currentStepIndex || $[5] !== field || $[6] !== fieldError || $[7] !== formId || $[8] !== register || $[9] !== renderMode) {
    t3 = <Component {...field} registerAction={register} control={control} error={fieldError} required={field.required} currentStepIndex={currentStepIndex} formId={formId} renderMode={renderMode} />;
    $[2] = Component;
    $[3] = control;
    $[4] = currentStepIndex;
    $[5] = field;
    $[6] = fieldError;
    $[7] = formId;
    $[8] = register;
    $[9] = renderMode;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};
export const FormFieldRenderer: React.FC<FormFieldRendererProperties> = t0 => {
  const $ = _c(15);
  const {
    section,
    currentStepIndex,
    formId,
    renderMode: t1
  } = t0;
  const renderMode = t1 === undefined ? "all" : t1;
  let t2;
  if ($[0] !== renderMode || $[1] !== section.sectionTitle) {
    t2 = renderMode !== "main" && typeof section.sectionTitle === "string" && section.sectionTitle !== "" && <SubheadingH3 className="mt-0">{section.sectionTitle}</SubheadingH3>;
    $[0] = renderMode;
    $[1] = section.sectionTitle;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== currentStepIndex || $[4] !== formId || $[5] !== renderMode || $[6] !== section.fields) {
    let t4;
    if ($[8] !== currentStepIndex || $[9] !== formId || $[10] !== renderMode) {
      t4 = (field, index) => {
        if (field.blockType === "conditionedBlock") {
          return <ConditionedField key={(typeof field.id === "string" && field.id !== "" ? field.id : undefined) ?? index} block={field} currentStepIndex={currentStepIndex} formId={formId} renderMode={renderMode} />;
        }
        return <SingleField key={("id" in field && typeof field.id === "string" && field.id !== "" ? field.id : undefined) ?? index} field={field} currentStepIndex={currentStepIndex} formId={formId} renderMode={renderMode} />;
      };
      $[8] = currentStepIndex;
      $[9] = formId;
      $[10] = renderMode;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    t3 = section.fields.map(t4);
    $[3] = currentStepIndex;
    $[4] = formId;
    $[5] = renderMode;
    $[6] = section.fields;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[12] !== t2 || $[13] !== t3) {
    t4 = <div className="space-y-4">{t2}{t3}</div>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
};